import * as React from 'react';
import { cn } from '../../lib/utils';
import { LiveBadge } from './live-badge';
import { LatencyChip } from './latency-chip';
import { useDesignSystemLabels, type CameraTileLabels } from '../ui/labels';

export interface CameraTileProps extends React.HTMLAttributes<HTMLDivElement> {
  cameraName: string;
  siteName?: string;
  status: 'live' | 'offline' | 'connecting';
  latencyMs?: number;
  /** Lets a transport-owning child report video state independently of camera health. */
  statusIndicator?: React.ReactNode;
  /** Preserves transport retry state when a separate health probe reports offline. */
  forceRenderContent?: boolean;
  /** Translated badge and offline words; the app-wide provider supplies them otherwise. */
  labels?: Partial<CameraTileLabels>;
  /** Slot for the actual <video> element (WebRTC) or a thumbnail. */
  children?: React.ReactNode;
}

/**
 * Video tile — instrument-frame around a live or playback stream.
 *
 * Sharp 3px corners, hairline border, deep graphite-950 background (NOT pure
 * black — keeps the warm palette consistent). Status badge + latency chip
 * top-left. Camera name + site bottom-left, both monospace.
 */
const CameraTile = React.forwardRef<HTMLDivElement, CameraTileProps>(
  (
    {
      cameraName,
      siteName,
      status,
      latencyMs,
      statusIndicator,
      forceRenderContent = false,
      labels: labelOverrides,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const labels = { ...useDesignSystemLabels().camera, ...labelOverrides };
    return (
      <div
        ref={ref}
        className={cn(
          'group relative aspect-video overflow-hidden rounded-sm border border-[var(--color-hairline)]',
          'bg-[var(--color-graphite-950)] text-white',
          className,
        )}
        {...props}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          {status === 'offline' && !forceRenderContent ? (
            <div className="flex flex-col items-center gap-2 text-white/60">
              <svg
                viewBox="0 0 32 32"
                width="32"
                height="32"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="5" y="10" width="18" height="14" rx="0.5" />
                <path d="M23 14l5-3v12l-5-3" />
                <path d="M3 3l26 26" />
              </svg>
              <p className="font-mono text-[0.75rem] uppercase tracking-[0.22em]">
                {labels.cameraOffline}
              </p>
            </div>
          ) : (
            children
          )}
        </div>

        {/* Top strip — status + latency */}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-1.5 p-2">
          {statusIndicator === undefined ? (
            <LiveBadge label={labels[status]} active={status === 'live'} />
          ) : (
            statusIndicator
          )}
          {typeof latencyMs === 'number' && status === 'live' && <LatencyChip ms={latencyMs} />}
        </div>

        {/* Bottom strip — camera + site identity, monospace */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 px-2 pb-2 pt-6 bg-gradient-to-t from-[var(--color-graphite-950)]/80 to-transparent">
          <div className="min-w-0">
            <p className="truncate font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-white">
              {cameraName}
            </p>
            {siteName && (
              <p className="truncate font-mono text-[0.75rem] uppercase tracking-[0.18em] text-white/60">
                {siteName}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  },
);
CameraTile.displayName = 'CameraTile';

export { CameraTile };
